export const actions = ['create', 'read', 'update', 'delete'] as const
// 'own' means the user is the creator of the entity or is assigned to it.
// 'any' means the user can access all entities of that type (for admins only).
export const accesses = ['own', 'any'] as const
export const entities = [
	'user',
	'platform_invitation',
	'platform_role',
	'platform_analytics',
	'nightclub',
	'nightclub_invitation',
	'nightclub_role',
	'nightclub_financials',
	'event',
	'event_invitation',
	'event_role',
	'event_financials',
	'ticket',
	'ticket_type',
	'purchase_session',
	'affiliate_link',
] as const

export type Action = (typeof actions)[number]
export type Access = (typeof accesses)[number]
export type Entity = (typeof entities)[number]

export type Permission = { action: Action; access: Access; entity: Entity }

export type PermissionString = `${Action}:${Access}:${Entity}`

export function parsePermissionString(perm: PermissionString): Permission {
	const [action, access, entity] = perm.split(':') as [Action, Access, Entity]
	return { action, access, entity }
}

export function getPermissionString({
	action,
	access,
	entity,
}: Permission): PermissionString {
	return `${action}:${access}:${entity}`
}

export const platformRoles = {
	user: {
		name: 'user',
		description: 'Default user role with basic permissions',
	},
	admin: {
		name: 'admin',
		description: 'Platform administrator with full permissions',
	},
	prospective_owner: {
		name: 'prospective_owner',
		description: 'User who can create nightclubs',
	},
} as const

export const nightclubRoles = {
	owner: {
		name: 'owner',
		description: 'Nightclub owner with full nightclub management permissions',
	},
	manager: {
		name: 'manager',
		description:
			'Nightclub manager with event and ticket management permissions',
	},
	promoter: {
		name: 'promoter',
		description: 'Nightclub promoter with event creation permissions',
	},
	pr: {
		name: 'pr',
		description: 'Nightclub PR specialist with read-only access',
	},
	accounting: {
		name: 'accounting',
		description: 'Nightclub accountant with ticket read access',
	},
	security: {
		name: 'security',
		description: 'Nightclub security with event read access',
	},
}

// System nightclub roles that cannot be deleted or renamed
export const SYSTEM_NIGHTCLUB_ROLES = ['owner', 'manager', 'promoter'] as const
export type SystemNightclubRole = (typeof SYSTEM_NIGHTCLUB_ROLES)[number]

export const isSystemNightclubRole = (
	roleName: string,
): roleName is SystemNightclubRole =>
	SYSTEM_NIGHTCLUB_ROLES.includes(roleName as SystemNightclubRole)

export const ownerPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'nightclub' },
	{ action: 'update', access: 'own', entity: 'nightclub' },
	{ action: 'delete', access: 'own', entity: 'nightclub' },
	{ action: 'read', access: 'own', entity: 'nightclub_financials' },
	{ action: 'create', access: 'own', entity: 'nightclub_role' },
	{ action: 'read', access: 'own', entity: 'nightclub_role' },
	{ action: 'update', access: 'own', entity: 'nightclub_role' },
	{ action: 'delete', access: 'own', entity: 'nightclub_role' },
	{ action: 'create', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'update', access: 'own', entity: 'event' },
	{ action: 'delete', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event_financials' },
	{ action: 'create', access: 'own', entity: 'ticket' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'update', access: 'own', entity: 'ticket' },
	{ action: 'delete', access: 'own', entity: 'ticket' },
	{ action: 'create', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'ticket_type' },
	{ action: 'update', access: 'own', entity: 'ticket_type' },
	{ action: 'delete', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'purchase_session' },
	{ action: 'create', access: 'own', entity: 'nightclub_invitation' },
	{ action: 'read', access: 'own', entity: 'nightclub_invitation' },
	{ action: 'delete', access: 'own', entity: 'nightclub_invitation' },
	{ action: 'create', access: 'own', entity: 'affiliate_link' },
	{ action: 'read', access: 'own', entity: 'affiliate_link' },
	{ action: 'update', access: 'own', entity: 'affiliate_link' },
	{ action: 'delete', access: 'own', entity: 'affiliate_link' },
]

export const managerPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'nightclub' },
	{ action: 'update', access: 'own', entity: 'nightclub' },
	{ action: 'read', access: 'own', entity: 'nightclub_financials' },
	{ action: 'create', access: 'own', entity: 'nightclub_role' },
	{ action: 'read', access: 'own', entity: 'nightclub_role' },
	{ action: 'update', access: 'own', entity: 'nightclub_role' },
	{ action: 'delete', access: 'own', entity: 'nightclub_role' },
	{ action: 'create', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'update', access: 'own', entity: 'event' },
	{ action: 'delete', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event_financials' },
	{ action: 'create', access: 'own', entity: 'ticket' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'update', access: 'own', entity: 'ticket' },
	{ action: 'delete', access: 'own', entity: 'ticket' },
	{ action: 'create', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'ticket_type' },
	{ action: 'update', access: 'own', entity: 'ticket_type' },
	{ action: 'delete', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'purchase_session' },
	{ action: 'create', access: 'own', entity: 'nightclub_invitation' },
	{ action: 'read', access: 'own', entity: 'nightclub_invitation' },
	{ action: 'delete', access: 'own', entity: 'nightclub_invitation' },
	{ action: 'create', access: 'own', entity: 'affiliate_link' },
	{ action: 'read', access: 'own', entity: 'affiliate_link' },
	{ action: 'update', access: 'own', entity: 'affiliate_link' },
	{ action: 'delete', access: 'own', entity: 'affiliate_link' },
]

export const promoterPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'nightclub' },
	{ action: 'create', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'update', access: 'own', entity: 'event' },
	{ action: 'delete', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event_financials' },
	{ action: 'create', access: 'own', entity: 'ticket' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'update', access: 'own', entity: 'ticket' },
	{ action: 'delete', access: 'own', entity: 'ticket' },
	{ action: 'create', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'ticket_type' },
	{ action: 'update', access: 'own', entity: 'ticket_type' },
	{ action: 'delete', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'purchase_session' },
	{ action: 'create', access: 'own', entity: 'affiliate_link' },
	{ action: 'read', access: 'own', entity: 'affiliate_link' },
	{ action: 'update', access: 'own', entity: 'affiliate_link' },
	{ action: 'delete', access: 'own', entity: 'affiliate_link' },
]

// PR permissions - read-only access for public relations activities
export const prPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'nightclub' },
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'create', access: 'own', entity: 'nightclub_invitation' },
	{ action: 'read', access: 'own', entity: 'nightclub_invitation' },
	{ action: 'read', access: 'own', entity: 'affiliate_link' },
]

// Accounting permissions - financial data access
export const accountingPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'nightclub' },
	{ action: 'read', access: 'own', entity: 'nightclub_financials' },
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event_financials' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'read', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'purchase_session' },
]

// Security permissions - event and venue access
export const securityPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'nightclub' },
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'update', access: 'own', entity: 'ticket' }, // For ticket validation
]

// Platform Admin permissions - comprehensive system oversight
export const adminPermissions: Permission[] = [
	// User management
	{ action: 'create', access: 'any', entity: 'user' },
	{ action: 'read', access: 'any', entity: 'user' },
	{ action: 'update', access: 'any', entity: 'user' },
	{ action: 'delete', access: 'any', entity: 'user' },

	// Platform role management
	{ action: 'create', access: 'any', entity: 'platform_role' },
	{ action: 'read', access: 'any', entity: 'platform_role' },
	{ action: 'update', access: 'any', entity: 'platform_role' },
	{ action: 'delete', access: 'any', entity: 'platform_role' },

	// Platform invitations
	{ action: 'create', access: 'any', entity: 'platform_invitation' },
	{ action: 'read', access: 'any', entity: 'platform_invitation' },
	{ action: 'update', access: 'any', entity: 'platform_invitation' },
	{ action: 'delete', access: 'any', entity: 'platform_invitation' },

	// Platform analytics
	{ action: 'read', access: 'any', entity: 'platform_analytics' },

	// System oversight - all nightclubs and events
	{ action: 'read', access: 'any', entity: 'nightclub' },
	{ action: 'update', access: 'any', entity: 'nightclub' },
	{ action: 'read', access: 'any', entity: 'nightclub_role' },
	{ action: 'read', access: 'any', entity: 'nightclub_financials' },
	{ action: 'read', access: 'any', entity: 'event' },
	{ action: 'read', access: 'any', entity: 'event_role' },
	{ action: 'read', access: 'any', entity: 'event_financials' },
	{ action: 'read', access: 'any', entity: 'ticket' },
	{ action: 'read', access: 'any', entity: 'ticket_type' },
	{ action: 'read', access: 'any', entity: 'purchase_session' },
]

// Basic user permissions
export const userPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'user' },
	{ action: 'update', access: 'own', entity: 'user' },
]

// Prospective owner permissions
export const prospectiveOwnerPermissions: Permission[] = [
	...userPermissions,
	{ action: 'create', access: 'own', entity: 'nightclub' },
]

// Event role templates
export const eventRoles = {
	organizer: {
		name: 'organizer',
		description: 'Event organizer with full event management permissions',
	},
	ticket_manager: {
		name: 'ticket_manager',
		description:
			'Event ticket manager with ticket sales and management permissions',
	},
	promoter: {
		name: 'promoter',
		description: 'Event promoter with limited event access',
	},
	security: {
		name: 'security',
		description: 'Event security with validation permissions',
	},
	coordinator: {
		name: 'coordinator',
		description: 'Event coordinator with operational permissions',
	},
}

// Event role permissions
export const eventOrganizerPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'update', access: 'own', entity: 'event' },
	{ action: 'delete', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event_financials' },
	{ action: 'create', access: 'own', entity: 'event_role' },
	{ action: 'read', access: 'own', entity: 'event_role' },
	{ action: 'update', access: 'own', entity: 'event_role' },
	{ action: 'delete', access: 'own', entity: 'event_role' },
	{ action: 'create', access: 'own', entity: 'event_invitation' },
	{ action: 'read', access: 'own', entity: 'event_invitation' },
	{ action: 'delete', access: 'own', entity: 'event_invitation' },
	{ action: 'create', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'ticket_type' },
	{ action: 'update', access: 'own', entity: 'ticket_type' },
	{ action: 'delete', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'update', access: 'own', entity: 'ticket' },
	{ action: 'read', access: 'own', entity: 'purchase_session' },
	{ action: 'create', access: 'own', entity: 'affiliate_link' },
	{ action: 'read', access: 'own', entity: 'affiliate_link' },
	{ action: 'update', access: 'own', entity: 'affiliate_link' },
	{ action: 'delete', access: 'own', entity: 'affiliate_link' },
]

export const eventTicketManagerPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event_financials' },
	{ action: 'create', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'ticket_type' },
	{ action: 'update', access: 'own', entity: 'ticket_type' },
	{ action: 'delete', access: 'own', entity: 'ticket_type' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'update', access: 'own', entity: 'ticket' },
	{ action: 'read', access: 'own', entity: 'purchase_session' },
]

export const eventPromoterPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'event_financials' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'create', access: 'own', entity: 'affiliate_link' },
	{ action: 'read', access: 'own', entity: 'affiliate_link' },
	{ action: 'update', access: 'own', entity: 'affiliate_link' },
	{ action: 'delete', access: 'own', entity: 'affiliate_link' },
]

export const eventSecurityPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'update', access: 'own', entity: 'ticket' }, // For validation
]

export const eventCoordinatorPermissions: Permission[] = [
	{ action: 'read', access: 'own', entity: 'event' },
	{ action: 'update', access: 'own', entity: 'event' },
	{ action: 'read', access: 'own', entity: 'ticket' },
	{ action: 'read', access: 'own', entity: 'ticket_type' },
]
